import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// Format date to readable string
export function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

// Format date + time (for logs/audit entries): "Apr 23, 2026 13:45:22"
export function formatDateTime(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
    const timePart = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    return `${datePart} ${timePart}`;
}

// Format date to relative time
export function formatRelativeDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return formatDate(dateString);
}

// Format duration in minutes to readable string
export function formatDuration(minutes) {
    if (!minutes || minutes === 0) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
}

// Format seconds to HH:MM:SS
export function formatTimer(seconds) {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Get initials from name
export function getInitials(name) {
    if (!name) return '?';
    return name
        .split(' ')
        .map(part => part[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

// Priority colors and labels
export const priorityConfig = {
    urgent: { label: 'Urgent', color: '#F26B6B', bgClass: 'priority-urgent' },
    high: { label: 'High', color: '#F5C26B', bgClass: 'priority-high' },
    medium: { label: 'Medium', color: '#6EC1E4', bgClass: 'priority-medium' },
    low: { label: 'Low', color: '#7ED957', bgClass: 'priority-low' },
    none: { label: 'None', color: '#9FB0AC', bgClass: 'priority-none' },
};

export const CONST_WORKFLOW_CONFIG = {
    notstarted: { name: 'Not Started', tolerableStatuses: ['todo'], color: '#F26B6B', bgClass: 'priority-urgent' },
    active: { name: 'Active', tolerableStatuses: ['inprogress'], color: '#F5C26B', bgClass: 'priority-high' },
    review: { name: 'Review', tolerableStatuses: ['review', 'onreview'], color: '#6EC1E4', bgClass: 'priority-medium' },
    done: { name: 'Done', tolerableStatuses: ['done'] ,color: '#7ED957', bgClass: 'priority-low' },
};

export const CONST_FUNC_CLEAN_STATUS = (status) => status?.toLowerCase().replace(/\s+/g, '');

export const CONST_WORKFLOW_CONFIG_ARR = Object.values(CONST_WORKFLOW_CONFIG);


export const statusColors = {
    'To Do': '#9FB0AC',
    'In Progress': '#6EC1E4',
    'Review': '#F5C26B',
    'Done': '#7ED957',
};

// Get status class
export function getStatusClass(status) {
    const normalized = status?.toLowerCase().replace(/\s+/g, '-');
    switch (normalized) {
        case 'to-do':
            return 'status-todo';
        case 'in-progress':
            return 'status-in-progress';
        case 'review':
            return 'status-review';
        case 'done':
            return 'status-done';
        default:
            return 'status-todo';
    }
}

// Check if date is overdue
export function isOverdue(dateString) {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
}

// Generate random color
export function generateColor() {
    const colors = ['#2F6F63', '#7ED957', '#F5C26B', '#6EC1E4', '#F26B6B', '#A7E3C8'];
    return colors[Math.floor(Math.random() * colors.length)];
}

// Debounce function
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// Custom field type icons and labels
export const customFieldTypes = {
    text: { label: 'Text', icon: 'Type' },
    number: { label: 'Number', icon: 'Hash' },
    date: { label: 'Date', icon: 'Calendar' },
    dropdown: { label: 'Dropdown', icon: 'ChevronDown' },
    multi_select: { label: 'Multi-select', icon: 'List' },
    checkbox: { label: 'Checkbox', icon: 'CheckSquare' },
    url: { label: 'URL', icon: 'Link' },
    currency: { label: 'Currency', icon: 'DollarSign' },
};

// Returns a user-facing error message for file upload failures.
// Pass the axios error; caller is responsible for showing toast.
export function getUploadErrorMessage(error) {
    if (error?.response?.status === 413) {
        const detail = error.response?.data?.detail || '';
        if (detail.toLowerCase().includes('quota')) {
            return 'Storage workspace penuh. Hapus file lama atau hubungi admin.';
        }
        return 'File terlalu besar.';
    }
    return 'Gagal mengupload file.';
}

// Format currency to Indonesian Rupiah
export function formatCurrency(value) {
    if (!value && value !== 0) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}
